import { tagged } from "@mirage/service-logging";
import { namespace } from "@mirage/service-operational-metrics";
import { TYPEAHEAD_SEARCH_SECTIONS } from "@mirage/service-typeahead-search/service/sections";
import instrument from "@mirage/service-typeahead-search/service/utils/instrument";
import type { Observable } from "rxjs";
import * as rx from "rxjs";
import * as op from "rxjs/operators";
import { predicateFactory } from "../utils/predicate-factory";
import { buildPipelineFunctions, getSources } from "./pipeline";
import { getLimits, performCacheAnalysis } from "./utils";

import type { TypeaheadCache } from "@mirage/service-typeahead-search/service/typeahead-cache";
import type { typeahead } from "@mirage/service-typeahead-search/service/types";
import { v4 as uuidv4 } from "uuid";

const logger = tagged("service-typeahead-search/search");
const metrics = namespace("typeahead-search");
export const INSTRUMENT_DEBUG = false;
INSTRUMENT_DEBUG satisfies false;

/**
 * Returns an observable of arrays of typeahead search results
 * It will handle collecting, deduplication, scoring, grouping, limiting, and
 * sorting of the results in each emitted array.
 *
 * There's a diagram of the data flow in confluence:
 * https://dropbox.atlassian.net/wiki/spaces/Dash/pages/1569784549/service-typeahead-search+-+search+function+diagram
 */
export default function search(
  untrimmedQuery: string,
  mode: typeahead.Mode,
  cache: TypeaheadCache,
  auxiliarySources: typeahead.PredicateSubject<typeahead.SourceId>[],
  config: typeahead.Config,
  limit: number = 5,
): Observable<{ effectiveQuery: string; results: typeahead.ScoredResult[] }> {
  return rx.of({ untrimmedQuery, mode, config }).pipe(
    op.switchMap(({ untrimmedQuery, mode, config }) => {
      const query = untrimmedQuery.trim();
      const pipelineUuid = uuidv4();

      const resultsWrapper = (results: typeahead.ScoredResult[]) => ({
        effectiveQuery: query,
        results,
      });

      // get section limits and total limits
      const [grossLimit, sectionLimit] = getLimits(query, mode, limit, config);

      const cacheAnalysis = performCacheAnalysis(cache);

      const callableSections = predicateFactory<typeahead.SectionId>(
        untrimmedQuery,
        mode,
        config.isOmniboxEnabled ? TYPEAHEAD_SEARCH_SECTIONS : [],
        {
          config,
          cacheAnalysis,
        },
      );

      const sources$ = rx.from(callableSections).pipe(
        op.toArray(),
        op.switchMap((sectionsArray) =>
          getSources(
            query,
            mode,
            {
              cache,
              sections: sectionsArray,
              config,
              grossLimit,
              sectionLimit,
              pipelineUuid,
            },
            auxiliarySources,
            cacheAnalysis,
          ),
        ),
      );

      const isources$ = instrument(
        sources$,
        (stats) => {
          if (INSTRUMENT_DEBUG) logger.debug(`sources`, stats);
          metrics.stats("search/sources", stats.duration, {
            name: "sources",
          });
        },
        false,
      );

      const pipelineOptions: typeahead.TaggedSearchOptions = {
        cache,
        sections: callableSections,
        config,
        grossLimit,
        sectionLimit,
        pipelineUuid,
      };

      let pipeline$ = isources$;

      const pipelineFunctions = buildPipelineFunctions(query, mode, config);

      for (const pipelineFn of pipelineFunctions) {
        pipeline$ = pipeline$.pipe(
          pipelineFn(
            query,
            mode,
            pipelineOptions,
            auxiliarySources,
            cacheAnalysis,
          ),
        );
      }

      const results$ = instrument(
        pipeline$,
        (stats) => {
          if (INSTRUMENT_DEBUG) logger.debug(`typeahead search`, stats);
          // it's possible we did not emit any results, do not pollute our ttfr values
          if (stats.first !== -1) metrics.stats("search/ttfr", stats.first);
          metrics.stats("search/duration", stats.duration);
          metrics.counter("search/status", 1, {
            status: stats.failed ? "error" : "success",
          });
        },
        false,
      );

      return results$.pipe(op.map(resultsWrapper));
    }),
  );
}
